import React, {memo, useCallback} from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid,
  Switch,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
} from "@mui/material";

import {FormPaperColorEnum, FormPrintColorEnum} from "../Types";
import {useAppStore} from "../Stores";
import {
  getLabelConfig,
  isPrintOptionOrientationAvailable,
  isPrintOptionPaperColorAvailable,
  isPrintOptionPrintColorAvailable
} from "../Utils";

export const PrintOptionsForm: React.FC = memo(() => {
  const labelType = useAppStore.use.labelType();
  const config = getLabelConfig(labelType);

  if (!config) {
    throw new Error('Impossible de récupérer la configuration du type d\'étiquette ' + labelType + '.');
  }

  const setPrintOptions = useAppStore.use.setPrintOptions();
  const printOptions = useAppStore.use.printOptions();

  if (!printOptions) {
    throw new Error('Impossible de récupérer les options d\'impression.');
  }

  const handlePrintColorChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPrintOptions({
        ...printOptions,
        [event.target.name]: event.target.checked ? FormPrintColorEnum.COLOR : FormPrintColorEnum.BW,
      });
    },
    [printOptions]
  );

  const handleSelectChange = useCallback(
    (event: SelectChangeEvent) => {
      setPrintOptions({
        ...printOptions,
        [event.target.name]: event.target.value,
      });
    },
    [printOptions]
  );

  return (
    <>
      <h2>Configuration d'impression</h2>

      <Grid container spacing={3} alignItems="center">
        <Grid item xs={6} sm={2}>
          <FormControl fullWidth>
            <InputLabel id="size-label" size={"small"}>
              Format
            </InputLabel>
            <Select
              size="small"
              labelId="size-label"
              id="size"
              name="size"
              value={printOptions.size as string}
              label="Format"
              onChange={handleSelectChange}
            >
              {config.printOptions.sizes.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={4}>
          <FormControl>
            <FormLabel id="orientation">Orientation étiquette</FormLabel>
            <RadioGroup
              row
              aria-labelledby="orientation-label"
              name="orientation"
              value={printOptions.orientation}
              onChange={handleSelectChange}
            >
              <FormControlLabel
                value='portrait'
                control={<Radio />}
                label="Portrait"
                disabled={!isPrintOptionOrientationAvailable(printOptions.size, 'portrait', config)}
              />
              <FormControlLabel
                value='landscape'
                control={<Radio />}
                label="Paysage"
                disabled={!isPrintOptionOrientationAvailable(printOptions.size, 'landscape', config)}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControlLabel
            control={
              <Switch
                color="primary"
                name="printColor"
                onChange={handlePrintColorChange}
              />
            }
            label="Impression couleur"
            labelPlacement="top"
            disabled={!isPrintOptionPrintColorAvailable(config)}
            checked={printOptions.printColor === FormPrintColorEnum.COLOR}
            defaultChecked={printOptions.printColor === FormPrintColorEnum.COLOR}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl>
            <FormLabel id="paper-label">Papier</FormLabel>
            <RadioGroup
              row
              aria-labelledby="paperColor-label"
              name="paperColor"
              value={printOptions.paperColor}
              onChange={handleSelectChange}
            >
              <FormControlLabel
                value={FormPaperColorEnum.WHITE}
                control={<Radio/>}
                label="Blanc"
                disabled={!isPrintOptionPaperColorAvailable(FormPaperColorEnum.WHITE, config)}
              />
              <FormControlLabel
                value={FormPaperColorEnum.YELLOW}
                control={<Radio/>}
                label="Jaune"
                disabled={!isPrintOptionPaperColorAvailable(FormPaperColorEnum.YELLOW, config)}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>
    </>
  );
});

export default PrintOptionsForm;
